import * as React from 'react';
import { Box, styled } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SettingDisplayText } from '../SettingDisplayText';

const PREFIX = 'MenuSupplementProviderPrefix';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

export enum EMenuSupplementProvider {
    TENKITES = 'TENKITES',
    NONE = 'NONE'
}

enum ETenKitesNutritionUnit {
    PER_SERVING = 'perServ',
    PER_HUNDRED = 'perHund',
    GDA_PERCENT = 'gdaPerc'
}

type IProp = {
    settings: IPublicAndPrivateSettings;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const MenuSupplementProviderSettings = ({ settings, onSubmit }: IProp) => {
    const getTenKitesNutritionLabel = React.useCallback((setting: string | undefined): string | undefined => {
        switch (setting) {
            case ETenKitesNutritionUnit.PER_HUNDRED:
                return 'Per 100g';
            case ETenKitesNutritionUnit.PER_SERVING:
                return 'Per Serving';
            case ETenKitesNutritionUnit.GDA_PERCENT:
                return '% of GDA';
            default:
                return setting;
        }
    }, []);

    const getMenuSupplementProviderLabel = React.useCallback((setting?: string): string => {
        switch (setting) {
            case EMenuSupplementProvider.TENKITES:
                return 'Ten Kites';
            case EMenuSupplementProvider.NONE:
            default:
                return 'None';
        }
    }, []);

    return (
        <StyledMuiCardForm
            title="Menu Supplement Provider"
            initialValues={{
                menuSupplementProvider: settings?.menuSupplementProvider ?? 'None',
                tenKitesOrgId: settings?.tenKites?.tenKitesOrgId,
                apiKey: settings?.tenKites?.apiKey,
                menuId: settings?.tenKites?.menuId,
                nutritionUnit: settings?.tenKites?.nutritionUnit
            }}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplayText
                        title="Provider"
                        text={getMenuSupplementProviderLabel(settings?.menuSupplementProvider)}
                    />
                    {settings?.menuSupplementProvider === EMenuSupplementProvider.TENKITES && (
                        <>
                            <SettingDisplayText
                                title="Ten Kites Organisation ID"
                                text={settings?.tenKites?.tenKitesOrgId}
                            />
                            <SettingDisplayText title="Menu ID" text={settings?.tenKites?.menuId} />
                            <SettingDisplayText title="API Key" text={settings?.tenKites?.apiKey} />
                            <SettingDisplayText
                                title="Nutrition Unit"
                                text={getTenKitesNutritionLabel(settings?.tenKites?.nutritionUnit)}
                            />
                        </>
                    )}
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Provider">
                        <SelectFormField
                            name="menuSupplementProvider"
                            options={[
                                { label: 'Ten Kites', value: EMenuSupplementProvider.TENKITES },
                                { label: 'None', value: EMenuSupplementProvider.NONE }
                            ]}
                        />
                    </SettingDisplay>
                    {settings?.menuSupplementProvider === EMenuSupplementProvider.TENKITES && (
                        <>
                            <SettingDisplay label="Ten Kites Organisation ID">
                                <TextFormField name="tenKitesOrgId" />
                            </SettingDisplay>
                            <SettingDisplay label="Menu ID">
                                <TextFormField name="menuId" />
                            </SettingDisplay>
                            <SettingDisplay label="API Key">
                                <TextFormField name="apiKey" />
                            </SettingDisplay>
                            <SettingDisplay label="Nutrition Unit">
                                <SelectFormField
                                    name="nutritionUnit"
                                    options={[
                                        {
                                            label: 'Per Serving',
                                            value: ETenKitesNutritionUnit.PER_SERVING
                                        },
                                        { label: 'Per 100g', value: ETenKitesNutritionUnit.PER_HUNDRED },
                                        { label: '% of GDA', value: ETenKitesNutritionUnit.GDA_PERCENT }
                                    ]}
                                />
                            </SettingDisplay>
                        </>
                    )}
                </Box>
            }
            onSubmit={onSubmit}
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
